import { BoundRadioGroupField, Carousel, Css, useComputed } from "@homebound/beam";
import type { FieldState } from "@homebound/form-state";
import { disableBasedOnPotentialOperation } from "src/routes/components/PotentialOperationsUtils";
import { ProductOptionCard } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/ProductOptionCard";
import type { SingleOrLinkedOptionCard_CheckoutConfigOptionGroupFragment } from "src/routes/projects/change-events/guided/workflows/addOrModifyOptions/components/SingleOrLinkedOptionCard.generated";
import { OptionPriceMode } from "src/routes/projects/change-events/guided/workflows/components/OptionPrice";
import { fail } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";

export type SingleOrLinkedOptionCardProps = {
  group: SingleOrLinkedOptionCard_CheckoutConfigOptionGroupFragment;
  /** The group's `childGroup`, when its options are the thumbnails under the group's single option. */
  childGroup?: SingleOrLinkedOptionCard_CheckoutConfigOptionGroupFragment;
  /** The form field that stores whether the group is included in the change. Without one there's no checkbox. */
  selectedField?: FieldState<boolean | null | undefined>;
  /** The form field that stores the `ReadyPlanOption` id picked from the thumbnails (the `childGroup`'s when given). */
  optionField: FieldState<string | null | undefined>;
  priceMode?: OptionPriceMode;
};

/**
 * Renders a single- or linked-product option group, e.g. flooring. It shows a `ProductOptionCard` for the selected
 * option, followed by a thumbnail carousel for picking between the group's options.
 *
 * Behaves like `ProductAddOnCard` in plan-configuration-frontend: the card falls back to the first option, so checking
 * the group commits whichever option is currently shown, and picking a thumbnail also includes the group.
 *
 * With a `childGroup`, the thumbnails are the child's options instead and the card at the top is the group's own option.
 */
export function SingleOrLinkedOptionCard(props: SingleOrLinkedOptionCardProps) {
  const { group, childGroup, selectedField, optionField, priceMode } = props;
  const selectedId = useComputed(() => optionField.value, [optionField]);
  const readOnly = useComputed(() => optionField.readOnly, [optionField]);
  const optionsGroup = childGroup ?? group;
  const pickedOption = optionsGroup.options.find((o) => o.option.id === selectedId);
  const selected =
    (childGroup ? group.options.first : pickedOption) ??
    group.options.first ??
    // A group always has at least one option, so an empty one is a data error and not something we should render around.
    fail(`Option group ${group.id} has no options`);
  const options = optionsGroup.options.map((o) => ({
    value: o.option.id,
    label: o.option.name,
    imgSrc:
      o.option.optionImage?.previewUrl ??
      o.primaryProduct?.featuredImage?.asset?.previewUrl ??
      PRODUCT_FALLBACK_IMG_URL,
    disabled: disableBasedOnPotentialOperation(o.canBeSelected),
  }));

  return (
    <div css={Css.df.fdc.gap2.$}>
      <ProductOptionCard
        group={group}
        option={selected}
        child={childGroup && { group: childGroup, option: pickedOption }}
        field={selectedField}
        priceMode={priceMode}
        onChange={(checked) => {
          selectedField?.set(checked);
          // Including the group commits the option the card is showing, which may just be the first-option fallback.
          if (checked && !childGroup) optionField.set(selected.option.id);
        }}
      />
      <Carousel>
        <BoundRadioGroupField
          field={optionField}
          label={optionsGroup.group.name}
          labelStyle="hidden"
          layout="thumbnail"
          disabled={readOnly}
          options={options}
          onChange={(id) => {
            optionField.set(id);
            // Picking an option also includes the group, so a selection never ends up on an excluded group.
            selectedField?.set(true);
          }}
        />
      </Carousel>
    </div>
  );
}
